import { VariantProps } from 'class-variance-authority'
import pluralize from 'pluralize'

import { ControlStatus } from '@/gen/controls/v1/control_status_pb'

import { controlStatusLabel } from '@/const/label'

import { EventProperties } from '@/lib/analytics/events'
import { useSelectableControlStatuses } from '@/lib/app/attested-status-access.hook'

import { Badge } from '@/components/lemmala-design-system/badge/badge'
import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'
import { ControlStatusChangeModal } from '@/components/shared/control-status-change-modal/control-status-change-modal'
import { ControlStatusIcon } from '@/components/shared/icons/control-status'
import { SubmitSelect, SubmitSelectProps } from '@/components/shared/submit-select/submit-select'

import { controlStatusVariant } from './variants'

type ControlStatusBadgeProps = {
  status: ControlStatus
  count?: number
  tooltip?: string
  showTooltip?: boolean
} & VariantProps<typeof controlStatusVariant>

export const ControlStatusBadge = ({
  status,
  count,
  variant = 'default',
  tooltip,
  showTooltip = false,
}: ControlStatusBadgeProps) => {
  let label = controlStatusLabel[status]
  if (count !== undefined) {
    label = status === ControlStatus.GAP ? pluralize(label, count, true) : `${count} ${label}`
  }

  const badge = (
    <Badge
      variant='outline'
      textCase='title'
      className={controlStatusVariant({
        status,
        variant,
      })}
    >
      <ControlStatusIcon status={status} />
      {label}
    </Badge>
  )

  if (showTooltip && tooltip) {
    return (
      <Tooltip trigger={badge} asChild>
        {tooltip}
      </Tooltip>
    )
  }

  return badge
}

type SelectControlStatusBadgeProps = {
  controlId: string
  status: ControlStatus
  controlTitle?: string
  originalAiStatus?: ControlStatus
  assessmentId?: string
  assessmentName?: string
  extraTrackingProps?: EventProperties
  readOnly?: boolean
  lastCalculatedAt?: Date
  /** 'headless' for the bare badge used in table cells, 'default' for a bordered standalone select. */
  triggerAppearance?: SubmitSelectProps<ControlStatus>['triggerAppearance']
  /** Lets a reviewer set a status the AI hasn't produced yet — the drawer allows it, table cells don't. */
  allowUnanalyzed?: boolean
  hasAttestation?: boolean
}

export const SelectControlStatusBadge = ({
  controlId,
  status,
  controlTitle,
  originalAiStatus,
  assessmentId,
  assessmentName,
  extraTrackingProps,
  readOnly = false,
  lastCalculatedAt,
  triggerAppearance = 'headless',
  allowUnanalyzed = false,
  hasAttestation = false,
}: SelectControlStatusBadgeProps) => {
  const selectableStatuses = useSelectableControlStatuses(
    hasAttestation || status === ControlStatus.ATTESTED,
  )

  // A bordered select carries its own field label, so an in-menu header would only repeat it.
  const menuTitle =
    triggerAppearance === 'headless' ? 'Set the validation status for this item' : undefined

  const isUnanalyzed = status === ControlStatus.NOT_EVALUATED || !lastCalculatedAt

  // Show non-clickable text without shield icon when the status can't be changed here
  if (readOnly || (isUnanalyzed && !allowUnanalyzed)) {
    return (
      <div className='px-2'>
        <ControlStatusBadge status={status} />
      </div>
    )
  }

  return (
    <ControlStatusChangeModal
      controlId={controlId}
      controlTitle={controlTitle}
      status={status}
      originalAiStatus={originalAiStatus}
      assessmentId={assessmentId}
      assessmentName={assessmentName}
      extraTrackingProps={extraTrackingProps}
      readOnly={readOnly}
    >
      {({ openChangeModal }) => (
        <SubmitSelect
          triggerAppearance={triggerAppearance}
          value={status}
          title={menuTitle}
          selectedValueRenderer={(value) => <ControlStatusBadge status={value} />}
          showCommentBox={false}
          showButtonsRow={false}
          onSelectChange={openChangeModal}
          menuItems={selectableStatuses.map((selectableStatus) => ({
            type: 'menuSelectItem' as const,
            label: <ControlStatusBadge status={selectableStatus} />,
            value: selectableStatus,
          }))}
        />
      )}
    </ControlStatusChangeModal>
  )
}
